// src/features/merchantConfig/lib/editFieldPermissions.js
//
// Per-field editability policy. The OMS server has NO allowlist (API doc
// §5.6 - every field on the typed value-block class is PATCH-able), so the
// UI is the only thing stopping a merchant from editing a field that is
// admin-only. This module is the single place that policy lives.
//
// Two-axis policy:
//   1. CATEGORY ALLOWLIST (`EDITABLE_CONFIG_KEYS`) - only configKeys in this
//      set can enter edit mode at all. Everything else is read-only end-to-end:
//      the toolbar's "Edit" button stays hidden and ArrayTable suppresses
//      add/delete affordances.
//   2. PER-CATEGORY FIELD BLOCKLIST (`POLICY_BY_CONFIG_KEY`) - within an
//      editable category, individual paths can still be blocked. Supports
//      exact dot-keys, exact bracketed keys (`pathToKey`), and regex
//      patterns matched against each path segment (used for "cost" /
//      "testing" heuristics where the schema has many cost_* or test_*
//      siblings).
//
// The path key is the JSON path joined by ".", matching `pathToDotKey`
// below - chosen because it's easy to read in code and matches what the
// product team would write in a doc.

import { pathToKey } from "./jsonPath";

/**
 * Convert a path array into a dot-joined string for permission lookups.
 *
 * @param {Array<string | number>} path
 * @returns {string}
 */
export function pathToDotKey(path) {
  if (!path || path.length === 0) return "";
  return path.map((s) => String(s)).join(".");
}

/**
 * Categories the merchant is allowed to edit. Any configKey NOT in this set
 * is read-only in the UI - the Edit button is hidden, drafts are never
 * created, and array add/delete buttons are suppressed.
 *
 * Per product (Phase 2 launch):
 *   - Order Summary Report
 *   - Scheduled Email Reports
 *   - SMS Notifications              (cost fields blocked)
 *   - WhatsApp Notifications         (cost + testing fields blocked)
 *   - Order Confirmation             (cost + testing fields blocked)
 *   - Webhooks
 *
 * @type {Set<string>}
 */
export const EDITABLE_CONFIG_KEYS = new Set([
  "MERCHANT_ORDER_SUMMARY_REPORT_CONFIGURATIONS",
  "MERCHANT_SCHEDULED_EMAIL_REPORT_CONFIGURATIONS",
  "MERCHANT_SMS_NOTIFICATION_CONFIGURATIONS",
  "MERCHANT_WHATSAPP_NOTIFICATION_CONFIGURATIONS",
  "MERCHANT_CREATE_ORDER_CONFIRMATION_CONFIGURATIONS",
  "MERCHANT_WEBHOOK_CONFIGURATIONS",
]);

/**
 * Is this configKey editable at all? Used by the toolbar to hide the
 * "Edit" button on read-only categories.
 *
 * @param {string | null | undefined} configKey
 * @returns {boolean}
 */
export function isCategoryEditable(configKey) {
  if (!configKey) return false;
  return EDITABLE_CONFIG_KEYS.has(configKey);
}

// Word-boundary regexes used by per-category blocklists. Each pattern is
// tested against every STRING segment of the path independently, so e.g.
// `["pricing", "sms_cost", "currency"]` matches the COST pattern via the
// middle segment.
const COST_PATTERN = /(?:^|_)costs?(?:$|_|\b)/i;
const TEST_PATTERN = /(?:^|_)test(?:ing|s)?(?:$|_|\b)/i;

/**
 * Per-configKey blocklists. ALWAYS use the configKey enum (the string the
 * URL takes - e.g. "MERCHANT_GENERAL_CONFIGURATIONS"), not the slug.
 *
 * @type {Record<string, {
 *   blockedPaths?: Set<string>,
 *   blockedExactPathKeys?: Set<string>,
 *   blockedPathPatterns?: RegExp[],
 * }>}
 */
const POLICY_BY_CONFIG_KEY = {
  MERCHANT_SMS_NOTIFICATION_CONFIGURATIONS: {
    // Cost changes must be done by ops, never by the merchant.
    blockedPathPatterns: [COST_PATTERN],
  },
  MERCHANT_WHATSAPP_NOTIFICATION_CONFIGURATIONS: {
    // Cost + testing knobs are ops-only.
    blockedPathPatterns: [COST_PATTERN, TEST_PATTERN],
  },
  MERCHANT_CREATE_ORDER_CONFIRMATION_CONFIGURATIONS: {
    // Cost + testing knobs are ops-only.
    blockedPathPatterns: [COST_PATTERN, TEST_PATTERN],
  },
};

/**
 * Is this field editable by the merchant?
 *
 * Returns `false` when EITHER:
 *   - the configKey isn't on the category allowlist (`EDITABLE_CONFIG_KEYS`), OR
 *   - the path matches a per-category blocklist entry (exact dot-key, exact
 *     bracketed key, or a regex pattern against any string segment).
 *
 * @param {{ configKey: string, path: Array<string | number> }} params
 * @returns {boolean}
 */
export function isFieldEditable({ configKey, path }) {
  if (!configKey) return false;
  if (!isCategoryEditable(configKey)) return false;

  const policy = POLICY_BY_CONFIG_KEY[configKey];
  if (!policy) return true;

  const dotKey = pathToDotKey(path);
  if (policy.blockedPaths && policy.blockedPaths.has(dotKey)) return false;

  const exactKey = pathToKey(path);
  if (
    policy.blockedExactPathKeys &&
    policy.blockedExactPathKeys.has(exactKey)
  ) {
    return false;
  }

  if (policy.blockedPathPatterns && policy.blockedPathPatterns.length > 0) {
    for (const seg of path) {
      if (typeof seg !== "string") continue;
      for (const pattern of policy.blockedPathPatterns) {
        if (pattern.test(seg)) return false;
      }
    }
  }

  return true;
}

/**
 * Audit fields that are server-stamped and must never appear in a PATCH body
 * (API doc §8.10). We strip these defensively in case they slip into a draft
 * via the raw-JSON path.
 *
 * @type {Set<string>}
 */
export const AUDIT_FIELDS = new Set([
  "created_date_time",
  "created_by",
  "updated_date_time",
  "updated_by",
]);

/**
 * Strip top-level audit fields from a value-block-shaped object.
 *
 * @param {Record<string, unknown>} valueBlock
 * @returns {Record<string, unknown>}
 */
export function stripAuditFields(valueBlock) {
  if (!valueBlock || typeof valueBlock !== "object") return valueBlock;
  const out = {};
  for (const k of Object.keys(valueBlock)) {
    if (!AUDIT_FIELDS.has(k)) out[k] = valueBlock[k];
  }
  return out;
}
